import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Calendar, Users, Layers3, RefreshCw, ChevronLeft, ChevronRight, Activity } from 'lucide-react';
import DepartmentGantt from '@/components/DepartmentGantt';

/** ProjectGanttPage — live timeline, 3 switchable views.
 *  Route: /gantt
 *  View modes: shot | artist | chapter
 */
export default function ProjectGanttPage() {
  const { api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [view, setView] = useState('shot');
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState('week'); // week | month | quarter
  const [scrollToday, setScrollToday] = useState(true);

  useEffect(() => {
    api.get('/projects').then(r => {
      setProjects(r.data);
      if (r.data.length) setProjectId(r.data[0].id);
    });
  }, []);

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [{ data }, tasksResp] = await Promise.all([
        api.get(`/reviews/gantt/${projectId}?view=${view}`),
        api.get(`/tasks?project_id=${projectId}`).catch(() => ({ data: [] })),
      ]);
      setData(data);
      setTasks(tasksResp.data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [projectId, view]);
  // Auto-refresh every 30s for "live"
  useEffect(() => {
    if (!projectId) return;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [projectId, view]);

  const timeline = useMemo(() => computeTimeline(data), [data]);

  return (
    <div className="p-6 space-y-4" data-testid="gantt-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <Activity className="h-5 w-5 text-amber-400 animate-pulse"/>
            Live Production Timeline
          </h1>
          <p className="text-[11px] text-zinc-500 mt-0.5">Updates every 30s · Data aggregated across all artists</p>
        </div>
        <select value={projectId} onChange={e=>setProjectId(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-white" data-testid="gantt-project">
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button onClick={load} disabled={loading} className="h-9 w-9 rounded bg-zinc-900 border border-zinc-800 hover:border-amber-400 text-zinc-400 hover:text-amber-400 flex items-center justify-center" data-testid="gantt-refresh">
          <RefreshCw className={`h-4 w-4 ${loading?'animate-spin':''}`}/>
        </button>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex rounded-lg border border-zinc-800 bg-zinc-900 p-0.5">
          {[
            { code:'shot',    label:'Per Shot',    Icon: Layers3 },
            { code:'artist',  label:'Per Artist',  Icon: Users },
            { code:'chapter', label:'Per Chapter', Icon: Calendar },
          ].map(({code, label, Icon}) => (
            <button key={code} onClick={()=>setView(code)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded ${view===code?'bg-amber-400 text-zinc-900 font-semibold':'text-zinc-400 hover:text-white'}`}
              data-testid={`view-${code}`}
            >
              <Icon className="h-3.5 w-3.5"/>{label}
            </button>
          ))}
        </div>
        <div className="flex rounded-lg border border-zinc-800 bg-zinc-900 p-0.5 ml-auto">
          {['week','month','quarter'].map(z => (
            <button key={z} onClick={()=>setZoom(z)}
              className={`px-3 py-1.5 text-xs rounded ${zoom===z?'bg-zinc-700 text-white':'text-zinc-400 hover:text-white'}`}
              data-testid={`zoom-${z}`}
            >{z}</button>
          ))}
        </div>
      </div>

      {/* KPI strip */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Rows" value={data.rows?.length || 0} accent="amber" />
          <KpiCard label="Total Bars" value={(data.rows || []).reduce((s,r)=>s + (r.bars?.length||0), 0)} accent="cyan" />
          <KpiCard label="Timeline" value={fmtRange(data.range)} accent="violet" />
          <KpiCard label="View" value={view} accent="emerald" />
        </div>
      )}

      {/* Department roll-up Gantt */}
      <DepartmentGantt tasks={tasks} project={projects.find(p => p.id === projectId)} />

      {/* Shot/Artist/Chapter drill-down Gantt */}

      {/* Gantt canvas */}
      <GanttCanvas data={data} timeline={timeline} zoom={zoom} loading={loading} view={view} scrollToday={scrollToday} />

      {/* Legend */}
      {data?.legend && (
        <div className="flex flex-wrap gap-2 pt-2">
          {Object.entries(data.legend).map(([k,c]) => (
            <div key={k} className="flex items-center gap-1.5 text-[10px] text-zinc-400 px-2 py-1 rounded bg-zinc-900/60 border border-zinc-800">
              <span className="h-2 w-3 rounded-sm" style={{background: c}} />
              {k.toUpperCase()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── TIMELINE HELPERS ──────────────────────────────────────── */

function computeTimeline(data) {
  if (!data?.range?.start_iso || !data?.range?.end_iso) return null;
  const start = new Date(data.range.start_iso);
  const end   = new Date(data.range.end_iso);
  // Pad 2 days each side for breathing room
  start.setDate(start.getDate() - 2);
  end.setDate(end.getDate() + 2);
  const total = Math.max(1, end.getTime() - start.getTime());
  return { start, end, totalMs: total };
}

function positionOf(iso, timeline) {
  if (!iso || !timeline) return 0;
  const t = new Date(iso).getTime();
  return Math.max(0, Math.min(1, (t - timeline.start.getTime()) / timeline.totalMs));
}

/* ─── GANTT CANVAS ─────────────────────────────────────────── */

function GanttCanvas({ data, timeline, zoom, loading, view, scrollToday }) {
  if (loading && !data) {
    return <div className="h-96 rounded-xl border border-zinc-800 bg-zinc-950 flex items-center justify-center text-sm text-zinc-500">Loading timeline…</div>;
  }
  if (!data || !data.rows?.length) {
    return <div className="h-96 rounded-xl border border-dashed border-zinc-800 bg-zinc-950 flex items-center justify-center text-sm text-zinc-500">No tasks scheduled for this project yet.</div>;
  }
  if (!timeline) {
    return <div className="h-96 rounded-xl border border-dashed border-zinc-800 bg-zinc-950 flex items-center justify-center text-sm text-zinc-500">Tasks exist but lack start/end dates — add deadlines in the Allotment page to populate the timeline.</div>;
  }

  // Generate time headers
  const headers = genHeaders(timeline, zoom);
  const rowWidth = Math.max(1000, headers.length * (zoom === 'week' ? 80 : zoom === 'month' ? 120 : 200));

  const todayPos = positionOf(new Date().toISOString(), timeline);

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden">
      <div className="flex">
        {/* Left label column */}
        <div className="w-56 shrink-0 border-r border-zinc-800 bg-zinc-950">
          <div className="h-12 border-b border-zinc-800 bg-zinc-900/50 px-3 flex items-center text-[11px] text-zinc-500 uppercase tracking-wider">
            {view === 'shot' ? 'Shot' : view === 'artist' ? 'Artist' : 'Chapter'}
          </div>
          {data.rows.map(r => (
            <div key={r.id} className="h-12 border-b border-zinc-900 px-3 flex items-center" data-testid={`gantt-row-label-${r.id}`}>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-white truncate">{r.label}</div>
                <div className="text-[10px] text-zinc-500 truncate">
                  {view === 'shot' && `Chapter ${r.meta?.chapter ?? '?'}`}
                  {view === 'artist' && `${r.meta?.task_count || 0} tasks · ${r.meta?.role || ''}`}
                  {view === 'chapter' && `${r.meta?.task_count || 0} tasks`}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Right timeline */}
        <div className="flex-1 overflow-x-auto relative">
          <div style={{ width: rowWidth, minWidth: '100%' }}>
            {/* Header bar */}
            <div className="h-12 border-b border-zinc-800 bg-zinc-900/50 flex relative">
              {headers.map((h,i) => (
                <div key={i} className="border-r border-zinc-800 px-2 py-2 text-[10px] text-zinc-400" style={{width: `${h.widthPct}%`}}>
                  <div className="font-semibold text-white">{h.label}</div>
                  <div className="text-[9px] text-zinc-500">{h.sub}</div>
                </div>
              ))}
            </div>
            {/* Rows */}
            <div className="relative">
              {/* Today marker */}
              <div className="absolute top-0 bottom-0 w-px bg-amber-400 z-20 pointer-events-none" style={{left: `${todayPos * 100}%`}}>
                <div className="absolute -top-0.5 -translate-x-1/2 bg-amber-400 text-zinc-900 text-[9px] font-bold px-1 rounded">TODAY</div>
              </div>
              {data.rows.map(r => (
                <div key={r.id} className="h-12 border-b border-zinc-900 relative hover:bg-zinc-900/30 group" data-testid={`gantt-row-${r.id}`}>
                  {/* weekly grid lines */}
                  {headers.map((_,i) => <div key={i} className="absolute top-0 bottom-0 w-px bg-zinc-900" style={{left: `${headers.slice(0,i+1).reduce((s,h)=>s+h.widthPct,0)}%`}} />)}
                  {/* bars */}
                  {r.bars.map((b,bi) => {
                    if (!b.start || !b.end) return null;
                    const left  = positionOf(b.start, timeline) * 100;
                    const right = positionOf(b.end,   timeline) * 100;
                    const width = Math.max(1.5, right - left);
                    return (
                      <div key={bi}
                        className="absolute top-2 h-8 rounded shadow-sm overflow-hidden cursor-pointer group/bar"
                        style={{left: `${left}%`, width: `${width}%`, background: `linear-gradient(90deg, ${b.color}55, ${b.color})`, borderLeft: `3px solid ${b.color}`}}
                        title={`${b.label || b.stage} · ${b.progress_pct}% · ${b.assignee_name || ''}`}
                        data-testid={`gantt-bar-${r.id}-${bi}`}
                      >
                        <div className="absolute top-0 left-0 bottom-0 bg-white/15" style={{width: `${b.progress_pct}%`}} />
                        <div className="relative h-full flex items-center px-2 text-[10px] font-semibold text-white truncate">
                          {(b.stage || '').toUpperCase()} · {b.progress_pct}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function genHeaders(timeline, zoom) {
  const out = [];
  const cursor = new Date(timeline.start);
  while (cursor < timeline.end) {
    const next = new Date(cursor);
    if (zoom === 'week')    next.setDate(next.getDate() + 7);
    if (zoom === 'month')   next.setMonth(next.getMonth() + 1);
    if (zoom === 'quarter') next.setMonth(next.getMonth() + 3);
    const widthPct = Math.min(100, (Math.min(next.getTime(), timeline.end.getTime()) - cursor.getTime()) / timeline.totalMs * 100);
    const label = zoom === 'week'
      ? `W${getWeekNum(cursor)}`
      : zoom === 'month'
        ? cursor.toLocaleString('default', { month: 'short' })
        : `Q${Math.floor(cursor.getMonth()/3)+1}`;
    const sub = cursor.toLocaleDateString('default', { month: 'short', day: '2-digit' });
    out.push({ label, sub, widthPct });
    cursor.setTime(next.getTime());
  }
  return out;
}

function getWeekNum(d) {
  const target = new Date(d);
  target.setHours(0,0,0,0);
  target.setDate(target.getDate() + 3 - (target.getDay() + 6) % 7);
  const week1 = new Date(target.getFullYear(), 0, 4);
  return 1 + Math.round(((target.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

function KpiCard({ label, value, accent = 'amber' }) {
  const map = {
    amber:  'from-amber-500/10 border-amber-400/30 text-amber-300',
    cyan:   'from-cyan-500/10 border-cyan-400/30 text-cyan-300',
    violet: 'from-violet-500/10 border-violet-400/30 text-violet-300',
    emerald:'from-emerald-500/10 border-emerald-400/30 text-emerald-300',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${map[accent]} to-transparent px-4 py-3`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-xl font-bold mt-0.5 text-white">{value}</div>
    </div>
  );
}

function fmtRange(r) {
  if (!r?.start_iso) return '—';
  const s = new Date(r.start_iso), e = new Date(r.end_iso);
  return `${s.toLocaleDateString('default',{month:'short',day:'2-digit'})} → ${e.toLocaleDateString('default',{month:'short',day:'2-digit'})}`;
}
